import { useEffect, useMemo, useState } from 'react';
import { formatLocationOption, getPlantCount } from '../../utils/Chloromance/utils/plants';
import type { CompanionMatches, HydrationState, Plant, PlantStatus } from '../../utils/Chloromance/types';
import { PlantCard } from './PlantCard';

type PlantGridProps = {
  plants: Plant[];
  selectedZone: string;
  statuses: Record<string, PlantStatus>;
  companionMatches: Record<string, CompanionMatches>;
  wateredPlantIds: string[];
  onWater: (plantId: string) => void;
  onEdit: (plant: Plant) => void;
  onDelete: (plant: Plant) => void;
};

type PlantGroup = {
  id: string;
  title: string;
  plants: Plant[];
  plantCount: number;
  critical: number;
  thirsty: number;
};

const STATUS_ORDER: HydrationState[] = ['Critical', 'Thirsty', 'Optimal'];

function getStatusRank(plant: Plant, statuses: Record<string, PlantStatus>) {
  return STATUS_ORDER.indexOf(statuses[plant.id]?.state || 'Optimal');
}

function countGroupStates(plants: Plant[], statuses: Record<string, PlantStatus>) {
  return plants.reduce(
    (counts, plant) => {
      const plantCount = getPlantCount(plant);
      counts.plantCount += plantCount;
      if (statuses[plant.id]?.state === 'Critical') counts.critical += plantCount;
      if (statuses[plant.id]?.state === 'Thirsty') counts.thirsty += plantCount;
      return counts;
    },
    { plantCount: 0, critical: 0, thirsty: 0 }
  );
}

type PlantGroupSectionProps = {
  group: PlantGroup;
  initiallyOpen: boolean;
  statuses: Record<string, PlantStatus>;
  companionMatches: Record<string, CompanionMatches>;
  wateredPlantIds: string[];
  onWater: (plantId: string) => void;
  onEdit: (plant: Plant) => void;
  onDelete: (plant: Plant) => void;
};

function PlantGroupSection({ group, initiallyOpen, statuses, companionMatches, wateredPlantIds, onWater, onEdit, onDelete }: PlantGroupSectionProps) {
  const [isOpen, setIsOpen] = useState(initiallyOpen);

  useEffect(() => {
    if (group.critical > 0) setIsOpen(true);
  }, [group.critical]);

  return (
    <details className="plant-group" open={isOpen} onToggle={(event) => setIsOpen(event.currentTarget.open)}>
      <summary className="plant-group-header">
        <span>
          <strong>{group.title}</strong>
          <small>{group.plantCount} plant{group.plantCount === 1 ? '' : 's'}</small>
        </span>
        <span className="plant-group-alerts">
          {group.critical > 0 && <b className="group-critical">{group.critical} critical</b>}
          {group.thirsty > 0 && <b className="group-thirsty">{group.thirsty} thirsty</b>}
          {group.critical === 0 && group.thirsty === 0 && <b className="group-ok">All OK</b>}
        </span>
        <span className="plant-card-chevron" aria-hidden="true" />
      </summary>
      <div className="plant-grid plant-grid-compact">
        {group.plants.map((plant) => (
          <PlantCard
            key={plant.id}
            plant={plant}
            status={statuses[plant.id]}
            matches={companionMatches[plant.id] || { companions: [], antagonists: [] }}
            justWatered={wateredPlantIds.includes(plant.id)}
            onWater={onWater}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    </details>
  );
}

export function PlantGrid({ plants, selectedZone, statuses, companionMatches, wateredPlantIds, onWater, onEdit, onDelete }: PlantGridProps) {
  const groups = useMemo<PlantGroup[]>(() => {
    if (selectedZone !== 'All Locations') {
      return STATUS_ORDER.map((state) => {
        const groupedPlants = plants.filter((plant) => statuses[plant.id]?.state === state);
        const counts = countGroupStates(groupedPlants, statuses);
        return { id: state, title: state, plants: groupedPlants, ...counts };
      }).filter((group) => group.plants.length > 0);
    }

    const byZone = plants.reduce<Record<string, Plant[]>>((map, plant) => {
      map[plant.chloromance_zone] ||= [];
      map[plant.chloromance_zone].push(plant);
      return map;
    }, {});

    return Object.entries(byZone)
      .map(([zone, groupedPlants]) => ({
        id: zone,
        title: formatLocationOption(zone),
        plants: [...groupedPlants].sort((a, b) => getStatusRank(a, statuses) - getStatusRank(b, statuses)),
        ...countGroupStates(groupedPlants, statuses),
      }))
      .sort((a, b) => b.critical - a.critical || b.thirsty - a.thirsty || a.title.localeCompare(b.title));
  }, [plants, selectedZone, statuses]);

  if (plants.length === 0) {
    return <section className="plant-empty-state" aria-label="Tracked plants"><p>No plants in this view.</p></section>;
  }

  return (
    <section className="plant-groups" aria-label="Tracked plants">
      {groups.map((group, index) => (
        <PlantGroupSection
          key={group.id}
          group={group}
          initiallyOpen={group.critical > 0 || (index === 0 && group.thirsty > 0) || groups.length === 1}
          statuses={statuses}
          companionMatches={companionMatches}
          wateredPlantIds={wateredPlantIds}
          onWater={onWater}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </section>
  );
}
